import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { SPECIALTY_SERVICES } from "@/data/site";
import { Icon } from "@/lib/Icon";
import { Reveal } from "@/lib/motionPrimitives";

export default function SpecialtyServices() {
  return (
    <section id="specialty" className="relative bg-surface py-24 md:py-32" data-testid="specialty-services">
      <div className="container-x">
        <Reveal>
          <p className="overline">Engineering Services</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Retrofitting, NDT &amp; Vibration Control
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            Beyond painting and coating, we deliver specialised engineering services that extend asset life, verify structural integrity and keep your plant running smoothly.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {SPECIALTY_SERVICES.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`specialty-card-${s.title}`}
              className="group relative flex flex-col overflow-hidden rounded-3xl border border-border bg-background"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={`${s.image}?auto=format&fit=crop&w=900&q=75`}
                  alt={s.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                <div className="absolute left-5 top-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                  <Icon name={s.icon} className="h-5 w-5" />
                </div>
                <h3 className="absolute bottom-4 left-5 pr-5 font-display text-xl font-bold text-white leading-tight">
                  {s.title}
                </h3>
              </div>

              <div className="flex flex-1 flex-col p-6">
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                <ul className="mt-5 grid gap-2.5">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm font-medium">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Check className="h-3 w-3" />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary"
                  data-testid={`specialty-quote-${s.title}`}
                >
                  Request a Quote
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
